import React, { useState, useEffect, Fragment } from 'react';
import './App.css';
import ReportView from './pages/ReportView';
import MapView from './pages/MapView';
import PlotView from './pages/Plot';

import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../Auth';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner'; // Import the loading spinner

import { ToastContainer } from 'react-toastify'; // Import ToastContainer
import { Bars3Icon } from '@heroicons/react/24/outline';

// import fakeDetectorsBackend from './pages/fakeDetectorsForBackend.txt'
// console.log(fakeDetectorsBackend); // Should log the file content as a string

// Define the structure of the object in the detector data
export interface DetectorObject {
  mac: string;
  ip: string;
  last_active: string;
  online: boolean;
  status: {
    site_name: string;
    gps_fix: boolean;
    gps_lat: number;
    gps_long: number;
    up_since: string;
    memory: string;
    fw_version: string;
    test_mode: boolean;
    thr: string;
    linux: boolean;
    sim_card_no?: string;
  };
}

export interface DetectorFile {
  filename: string;
  timestamp: string;
  filepath: string;
}

export type AllServerFilesObject = {
  [siteName: string]: DetectorFile[];
};

export interface NewServerFilesFormat {
  data: AllServerFilesObject;
  last_triggered: string;
}

const Backend: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [detectorData, setDetectorData] = useState<DetectorObject[] | null>([]);
  const [reportGenAt, setReportGenAt] = useState<string | null>(null);
  const [detectorDataBR, setDetectorDataBR] = useState<DetectorObject[] | null>(
    []
  );
  const [reportGenAtBR, setReportGenAtBR] = useState<string | null>(null);
  const [detectorDataAll, setDetectorDataAll] = useState<
    DetectorObject[] | null
  >([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [fileData, setfileData] = useState<AllServerFilesObject | null>(null);
  const [fileDataLastModified, setFileDataLastModified] = useState<
    string | null
  >(null);
  const [fileDataUSA, setfileDataUSA] = useState<AllServerFilesObject | null>(
    null
  );
  const [fileDataLastModifiedUSA, setFileDataLastModifiedUSA] = useState<
    string | null
  >(null);

  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);

  // Inside handleFilePathSelected
  const handleFilePathSelected = (
    filepath: string,
    source: 'dataBR' | 'dataUSA'
  ) => {
    setSelectedFilePath(filepath);
    // console.log(`Filepath selected from ${source}:`, filepath);

    const encodedPath = encodeURIComponent(filepath);
    window.open(
      `/fnnbackend/plot?filepath=${encodedPath}&source=${source}`,
      '_blank'
    );
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preferredColumnsOrder, setPreferredColumnsOrder] = useState([]);

  const [selectedView, setSelectedView] = useState(() => {
    return localStorage.getItem('selectedView') || 'report';
  });

  // Update localStorage whenever selectedView changes
  useEffect(() => {
    localStorage.setItem('selectedView', selectedView);
  }, [selectedView]);

  const handleViewChange = (view: React.SetStateAction<string>) => {
    setSelectedView(view);
  };

  const FNNlogo = require('../img/FNNLogoWithWhiteFont-07.png');

  const navigate = useNavigate();

  const { username, first_name } = useAuth();

  const handleAuthRedirection = () => {
    navigate('/login'); // Replace with your desired route
  };

  const [expandedRow, setExpandedRow] = useState(null); // Ref to store the expanded row

  const fetchDataViewList = ['report', 'map'];

  useEffect(() => {
    if (!fetchDataViewList.includes(selectedView)) return;

    const fetchData = async () => {
      try {
        const response = await fetch('/apidet/detector/monitoring', {
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        let alljson: any = {};
        let alljson2: any = {};

        // Check if dataUSA contains an error
        if (data.dataUSA && !data.dataUSA.error) {
          // If the raw response is a string, parse it to JSON
          const cleanedString = data.dataUSA.replace(/\\n/g, '').trim(); // Clean up the string
          const jsondata = JSON.parse(cleanedString);
          alljson = jsondata;

          // console.log(jsondata)

          // Remove the generated_at key if it exists
          if (jsondata.generated_at) {
            setReportGenAt(jsondata.generated_at);
            delete jsondata.generated_at; // Remove the key from the object
          }
          setDetectorData(jsondata);
        }

        // Same for BR Data
        // Check if dataUSA contains an error
        if (data.dataBR && !data.dataBR.error) {
          // If the raw response is a string, parse it to JSON
          const cleanedStringBR = data.dataBR.replace(/\\n/g, '').trim(); // Clean up the string
          const jsondataBR = JSON.parse(cleanedStringBR);
          alljson2 = jsondataBR;

          // Remove the generated_at key if it exists
          if (jsondataBR.generated_at) {
            setReportGenAtBR(jsondataBR.generated_at);
            delete jsondataBR.generated_at; // Remove the key from the object
          }
          setDetectorDataBR(jsondataBR);
        }

        setDetectorDataAll({ ...alljson, ...alljson2 });

        if (Object.keys(alljson).length || Object.keys(alljson2).length) {
          setIsLoading(false);
        }
      } catch (error) {
        //USA
        setDetectorData([]);
        setReportGenAt(null);
        //BR
        setDetectorDataBR([]);
        setReportGenAtBR(null);
        console.error('Error fetching data:', error);
      }
    };

    fetchData();

    // Fetch data every 5 seconds
    const interval = setInterval(fetchData, 5000); // temp

    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, [selectedView]);

  const fetchFileData = async (trigger?: string, source?: any) => {
    try {
      const url = new URL(
        '/apidet/fnnbackend/fnnMostRecentFilesView',
        window.location.origin
      );

      // Append the trigger parameter to the URL if it's provided
      if (trigger) {
        url.searchParams.append('trigger', trigger);
      }

      url.searchParams.append('origin', source);

      // Fetch data from Django API
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', // Or 'include' based on your need
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NewServerFilesFormat = await response.json();

      if (source === 'dataUSA') {
        setfileDataUSA(data.data); // Access nested 'data'
        setFileDataLastModifiedUSA(data.last_triggered); // Save last_triggered if you need it
      }

      if (source === 'dataBR') {
        setfileData(data.data); // Access nested 'data'
        setFileDataLastModified(data.last_triggered); // Save last_triggered if you need it
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (!fetchDataViewList.includes(selectedView)) return;
    // Fetch USA data without 'trigger' parameter
    fetchFileData(undefined, 'dataUSA');

    // Fetch BR data without 'trigger' parameter
    fetchFileData(undefined, 'dataBR');

    // Fetch data every 5 seconds (or adjust the interval as needed)
    const interval = setInterval(() => {
      fetchFileData(undefined, 'dataUSA');
      fetchFileData(undefined, 'dataBR');
    }, 50000); // Adjust this to a reasonable interval 50 seconds

    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, [selectedView]);

  return (
    <div className="AppBackend backend">
      <ToastContainer /> {/* Handle toasts in this page */}
      <header className="flex items-center justify-between px-5 header flex-wrap">
        {/* App Logo - left */}
        <a href="/" className="logo-container">
          <img src={FNNlogo} className="App-logo" alt="FNN logo" />
        </a>

        {/* Center Nav - only on desktop */}
        <div className="desktop-nav hidden sm:!flex absolute left-1/2 transform -translate-x-1/2">
          <button
            className={`bnt-header ${selectedView === 'map' ? 'active' : ''}`}
            onClick={() => handleViewChange('map')}
          >
            Map
          </button>
          <button
            className={`bnt-header ${
              selectedView === 'report' ? 'active' : ''
            }`}
            onClick={() => handleViewChange('report')}
          >
            Report
          </button>

          <button
            className={`bnt-header ${selectedView === 'plot' ? 'active' : ''}`}
            onClick={() => handleViewChange('plot')}
          >
            Plot
          </button>
        </div>

        {/* User Icon - right */}
        <div
          onClick={handleAuthRedirection}
          className="user-icon2 mr-3 flex gap-0 flex-col items-center text-base text-gray-400 hover:text-white cursor-pointer"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '25px', height: '25px' }}
          >
            <g id="SVGRepo_iconCarrier">
              <path
                d="M8 7C9.65685 7 11 5.65685 11 4C11 2.34315 9.65685 1 8 1C6.34315 1 5 2.34315 5 4C5 5.65685 6.34315 7 8 7Z"
                fill="#28aae1"
              />
              <path
                d="M14 12C14 10.3431 12.6569 9 11 9H5C3.34315 9 2 10.3431 2 12V15H14V12Z"
                fill="#28aae1"
              />
            </g>
          </svg>
          {first_name !== '' ? first_name : username.split('@')[0]}
        </div>

        {/* Menu Icon */}
        <div
          className="fnn-backend-icon-bg sm:hidden h-10 w-10 flex justify-center items-center responsive-margin"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Bars3Icon className="fnn-backend-icon text-white" />
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="mobile-sidebar">
          <button
            className={`sidebar-btn ${selectedView === 'map' ? 'active' : ''}`}
            onClick={() => {
              handleViewChange('map');
              setIsMobileMenuOpen(false);
            }}
          >
            Map
          </button>
          <button
            className={`sidebar-btn ${
              selectedView === 'report' ? 'active' : ''
            }`}
            onClick={() => {
              handleViewChange('report');
              setIsMobileMenuOpen(false);
            }}
          >
            Report
          </button>

          <button
            className={`sidebar-btn ${selectedView === 'plot' ? 'active' : ''}`}
            onClick={() => {
              handleViewChange('plot');
              setIsMobileMenuOpen(false);
            }}
          >
            Plot
          </button>
        </div>
      )}
      {selectedView === 'map' &&
        (isLoading ? (
          <LoadingSpinner />
        ) : (
          <div style={{ width: '100%', height: '100%' }}>
            {detectorDataAll && Object.keys(detectorDataAll).length === 0 ? (
              <h2
                style={{
                  color: '#616060',
                  position: 'absolute',
                  top: '30%',
                  left: '30%',
                  right: '30%',
                }}
              >
                No available detectors to be reported.
              </h2>
            ) : (
              <MapView detectorData={detectorDataAll || []} />
            )}
          </div>
        ))}
      {selectedView === 'report' &&
        (isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className={'body'}>
            {detectorData && Object.keys(detectorData).length === 0 ? (
              <h2 style={{ color: '#616060' }}>USA Report not available</h2>
            ) : (
              <ReportView
                detectorData={detectorData}
                fileData={fileDataUSA || null}
                reportGenAt={reportGenAt}
                preferredColumnsOrder={preferredColumnsOrder}
                expandedRow={expandedRow}
                setExpandedRow={setExpandedRow}
                onRefreshFileData={() => fetchFileData('trigger', 'dataUSA')}
                fileDataLastModified={fileDataLastModifiedUSA}
                onFilePathSelect={(filepath) =>
                  handleFilePathSelected(filepath, 'dataUSA')
                }
              />
            )}

            {detectorDataBR && Object.keys(detectorDataBR).length === 0 ? (
              <h2 style={{ color: '#616060' }}>BR Report not available</h2>
            ) : (
              <ReportView
                detectorData={detectorDataBR}
                fileData={fileData || null}
                reportGenAt={reportGenAtBR}
                preferredColumnsOrder={preferredColumnsOrder}
                expandedRow={expandedRow}
                setExpandedRow={setExpandedRow}
                onRefreshFileData={() => fetchFileData('trigger', 'dataBR')}
                fileDataLastModified={fileDataLastModified}
                onFilePathSelect={(filepath) =>
                  handleFilePathSelected(filepath, 'dataBR')
                }
              />
            )}
          </div>
        ))}
      {selectedView === 'plot' && (
        <div className={'body'}>
          <PlotView />
        </div>
      )}
    </div>
  );
};

export default Backend;
